"use client";

import React, { Suspense } from "react";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { tv } from "tailwind-variants";
import { SomethingWentWrong } from "@/components/organisms";
import RedirectWithParams from "@/components/utilities/RedirectWithParams";
import {
	ExitedFundTag,
	OfferTags,
} from "@/components/organisms/offer/OfferTags";
import { OffersDocument, CheckForOpenTradesDocument } from "./page.generated";
import { OfferCard, OfferCardLoading } from "./OfferCard";

const wrapper = tv({
	base: "max-w-container-lg container mx-auto px-5 pt-12 grid gap-4 md:grid-cols-2 lg:gap-6",
});

function LoadingState() {
	return (
		<div className={wrapper()}>
			{[1, 2, 3, 4].map((key) => (
				<OfferCardLoading key={key} />
			))}
		</div>
	);
}

function RedirectWhenNoOffers() {
	const { data, loading } = useQuery(CheckForOpenTradesDocument);

	if (loading) {
		return <LoadingState />;
	}

	const hasSellOrders =
		data?.sellOrders &&
		data.sellOrders.filter((sellOrder) => sellOrder.status === "OPEN").length >
			0;

	return (
		<Suspense fallback={null}>
			<RedirectWithParams url={hasSellOrders ? "/trade" : "/portfolio"} />
		</Suspense>
	);
}

export default function InvestPage() {
	const { data, loading, error } = useQuery(OffersDocument);

	if (loading) {
		return <LoadingState />;
	}

	if (error) {
		return <SomethingWentWrong trigger={error} className="mt-40" />;
	}

	const { funds, offers } = data!;

	const exitedFunds = funds.filter((fund) => !!fund.exitDetails);

	const sortedOffers = offers
		.filter((offer) => !offer.hideOffer)
		.sort((a, b) => {
			// Sort by status - open offers come first
			if (a.status === "OPEN" && b.status !== "OPEN") {
				return -1;
			}

			if (a.status !== "OPEN" && b.status === "OPEN") {
				return 1;
			}

			return (
				new Date(b.openDate ?? 0).getTime() -
				new Date(a.openDate ?? 0).getTime()
			);
		});

	if (sortedOffers.length === 0 && exitedFunds.length === 0) {
		return <RedirectWhenNoOffers />;
	}

	return (
		<div className={wrapper()}>
			{sortedOffers.map((offer) => (
				<OfferCard
					key={`Offer-${offer.id}`}
					data-testid={`offer-card-${offer.slug}`}
					title={offer.name}
					blurb={offer?.subtitle || offer.fund.summary.content}
					imageUrl={offer.cardImageUrl}
					tags={<OfferTags offer={offer} />}
					href={offer.status !== "CLOSED" ? `/invest/${offer.slug}` : undefined}
					grayscale={offer.status === "CLOSED"}
					details={offer.details}
				/>
			))}

			{exitedFunds.map((fund) => (
				<OfferCard
					key={`Fund-${fund.id}`}
					data-testid={`exited-fund-card-${fund.id}`}
					title={fund.name}
					blurb={fund.summary.content}
					imageUrl={fund.cardImageUrl}
					tags={<ExitedFundTag />}
					grayscale
				/>
			))}
		</div>
	);
}

gql`
	${OfferTags.fragment}
	query Offers {
		offers {
			id
			slug
			name
			cardImageUrl
			status
			hideOffer
			openDate
			digitalSubscription
			fund {
				id
				summary {
					content
				}
			}
			subtitle
			details {
				index
				key
				value
			}
			...OfferTags
		}
		funds {
			id
			name
			cardImageUrl
			summary {
				content
			}
			exitDetails {
				__typename
			}
		}
	}
	query CheckForOpenTrades {
		sellOrders {
			id
			status
		}
	}
`;
